/**
 * ELK Cargo goods rate classification.
 *
 * Freight is priced per CBM, but the rate depends on what the goods are.
 * This module turns a free-text goods description (English and/or Chinese —
 * the Guangzhou warehouse writes both) into a rate category, so rates are
 * generated automatically wherever goods are described: the client label
 * form, the warehouse spreadsheet import, and bill estimates.
 *
 * Rates below are the house defaults — edit them here in one place.
 */

export const RATE_CATEGORIES = [
  {
    key: "standard",
    label: "Standard",
    ratePerCbm: 260,
    currency: "USD",
    summary: "Clothes, shoes, bags, furniture and general dry goods",
  },
  {
    key: "electronics-fragile",
    label: "Electronics & Fragile",
    ratePerCbm: 300,
    currency: "USD",
    summary: "Electronics, appliances, machines, ceramics, tiles and glass",
  },
  {
    key: "special-care",
    label: "Special Care",
    ratePerCbm: 350,
    currency: "USD",
    summary: "Battery goods, food & supplements, cosmetics and liquids",
  },
  {
    key: "restricted",
    label: "Restricted",
    ratePerCbm: 400,
    currency: "USD",
    summary: "Raw batteries and paint at 1 CBM or more",
  },
] as const;

export type RateCategoryKey = (typeof RATE_CATEGORIES)[number]["key"];
export type RateCategory = (typeof RATE_CATEGORIES)[number];

export const MINIMUM_BILLABLE_CBM = 0.1;

export function getRateCategory(key: RateCategoryKey): RateCategory {
  return RATE_CATEGORIES.find((c) => c.key === key) ?? RATE_CATEGORIES[0];
}

export function isRateCategoryKey(value: string): value is RateCategoryKey {
  return RATE_CATEGORIES.some((c) => c.key === value);
}

// "sizeFlip" rules escalate Special Care → Restricted at >= 1 CBM.
// "perPieceQuote" goods can't be priced per CBM alone and need a manual quote.
interface KeywordRule {
  pattern: RegExp;
  category: RateCategoryKey;
  sizeFlip?: boolean;
  perPieceQuote?: boolean;
}

const KEYWORD_RULES: KeywordRule[] = [
  // Per-piece quote goods — checked first.
  {
    pattern: /e-?bike|motor\s?bike|motorcycle|scooter|tricycle|摩托|电动车|三轮车/i,
    category: "special-care",
    perPieceQuote: true,
  },

  // Restricted / size-flip goods: raw batteries and paint escalate in bulk.
  {
    pattern: /raw batter|lithium|power ?bank|电池/i,
    category: "special-care",
    sizeFlip: true,
  },
  // Battery-POWERED goods stay Special Care regardless of size.
  {
    pattern: /batter(y|ies)|带电/i,
    category: "special-care",
  },
  {
    pattern: /paint|油漆|涂料/i,
    category: "special-care",
    sizeFlip: true,
  },

  // Special care: food, supplements, cosmetics, liquids.
  {
    pattern:
      /food|supplement|vitamin|noodle|snack|drink|beverage|食品|食物|方便面|零食|维生素|软糖/i,
    category: "special-care",
  },
  {
    pattern:
      /cosmetic|cream|lotion|perfume|shampoo|soap|oil|liquid|化妆品|香水|洗发|液体|精油/i,
    category: "special-care",
  },

  // Electronics, appliances, machines.
  {
    pattern:
      /electronic|electrical|fitting|socket|switch|cable|charger|adapter|电子|电器|插座|开关|电线|充电/i,
    category: "electronics-fragile",
  },
  {
    pattern:
      /\btv\b|television|monitor|screen|phone|laptop|computer|tablet|camera|drone|speaker|电视|手机|电脑|相机|摄像|音响|音箱/i,
    category: "electronics-fragile",
  },
  {
    pattern:
      /fridge|freezer|microwave|blender|fan\b|air ?condition|appliance|machine|generator|tool|equipment|massage|ultra ?sound|冰箱|风扇|空调|机器|机|设备|工具|按摩椅/i,
    category: "electronics-fragile",
  },
  // Fragile ceramics, tiles, glass.
  {
    pattern: /ceramic|porcelain|tile|glass|mirror|dish|plate|bowl|陶瓷|瓷砖|玻璃|镜|餐具|碟|盘|碗/i,
    category: "electronics-fragile",
  },

  {
    pattern: /bulb|lamp|light|\bled\b|solar|torch|灯|太阳能/i,
    category: "electronics-fragile",
  },

  // Standard everyday goods.
  {
    pattern:
      /spare ?part|car ?part|auto ?part|brake|bumper|windscreen|tyre|tire|rim\b|配件|轮胎/i,
    category: "standard",
  },
  {
    pattern:
      /cloth|garment|apparel|dress|shirt|trouser|jean|shoe|slipper|sandal|sneaker|bag|handbag|luggage|fabric|textile|curtain|towel|bedding|衣服|服装|鞋|拖鞋|包|布|窗帘|毛巾|床上/i,
    category: "standard",
  },
  {
    pattern:
      /furniture|chair|table|sofa|bed\b|cabinet|door|wardrobe|家具|椅|桌|沙发|床|柜|门|木门/i,
    category: "standard",
  },
  {
    pattern:
      /toy|stationery|paper|book|pen\b|kitchen|cookware|pot\b|pan\b|utensil|hair|wig|accessor|玩具|文具|纸|书|厨房|锅|假发|饰品/i,
    category: "standard",
  },
];

export interface GoodsClassification {
  category: RateCategory;
  // The keyword that decided the category, null when nothing matched and the
  // default (standard) was applied — callers should show that as "unverified".
  matchedKeyword: string | null;
  // True when the goods need a manual per-piece quote (bikes, scooters).
  perPieceQuote: boolean;
}

export function classifyGoods(
  description: string,
  cbm?: number | null,
): GoodsClassification {
  const text = description.trim();
  if (text === "") {
    return {
      category: getRateCategory("standard"),
      matchedKeyword: null,
      perPieceQuote: false,
    };
  }

  let best: { rule: KeywordRule; matched: string } | null = null;
  for (const rule of KEYWORD_RULES) {
    const match = text.match(rule.pattern);
    if (match != null) {
      best = { rule, matched: match[0] };
      break;
    }
  }

  if (best == null) {
    return {
      category: getRateCategory("standard"),
      matchedKeyword: null,
      perPieceQuote: false,
    };
  }

  let categoryKey = best.rule.category;
  if (best.rule.sizeFlip === true && cbm != null && cbm >= 1) {
    categoryKey = "restricted";
  }

  return {
    category: getRateCategory(categoryKey),
    matchedKeyword: best.matched,
    perPieceQuote: best.rule.perPieceQuote === true,
  };
}

/**
 * Classify a set of description lines (e.g. warehouse sheet rows for one
 * tracking code) as a whole: the highest-rate category among the lines wins,
 * because mixed cargo is priced at the rate of its most expensive goods.
 */
export function classifyGoodsLines(
  descriptions: string[],
  totalCbm?: number | null,
): GoodsClassification {
  const combined = descriptions.filter((d) => d.trim() !== "");
  if (combined.length === 0) {
    return classifyGoods("", totalCbm);
  }

  let best: GoodsClassification | null = null;
  for (const description of combined) {
    const result = classifyGoods(description, totalCbm);
    if (
      best == null ||
      result.category.ratePerCbm > best.category.ratePerCbm ||
      (result.perPieceQuote && !best.perPieceQuote)
    ) {
      best = result;
    }
  }
  return best ?? classifyGoods("", totalCbm);
}

/**
 * Estimated freight cost for a CBM total: the classification's rate applied
 * to the volume, floored at the published minimum billable volume so the
 * figure never undercuts what the terms promise to bill. Callers should show
 * "price on request" instead when the classification is perPieceQuote.
 */
export function estimateFreightCost(
  classification: GoodsClassification,
  totalCbm: number,
): number {
  const billableCbm = Math.max(totalCbm, MINIMUM_BILLABLE_CBM);
  return billableCbm * classification.category.ratePerCbm;
}
